/**
 * API service layer
 * Handles all HTTP requests to the Django backend
 */

import axios, { AxiosError, AxiosInstance } from 'axios'
import type {
  MerchantData,
  MerchantApiResponse,
  UserPaymentData,
  UserPaymentApiResponse,
  PaymentRequest,
  PaymentResponse,
  ApiError,
  QRPaymentPayload,
  QRPaymentResponse,
} from '@/types'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types'
import { API_BASE_URL, API_ENDPOINTS } from '@/utils/constants'

/**
 * Creates and configures an Axios instance
 */
function createApiClient(): AxiosInstance {
  // Only add ngrok header in development
  const isDevelopment = import.meta.env.DEV || window.location.hostname.includes('ngrok')

  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
      ...(isDevelopment && { 'ngrok-skip-browser-warning': 'true' }), // Skip ngrok browser warning only in dev
    },
  })

  // Request interceptor for adding auth tokens if needed
  client.interceptors.request.use(
    (config) => {
      // Add any auth headers here if needed
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Extract error message from response
      let message = 'An unexpected error occurred'
      
      if (error.response) {
        // Try to get error message from response data
        const data = error.response.data as any
        if (data?.message) {
          message = data.message
        } else if (data?.error) {
          message = data.error
        } else if (typeof data === 'string') {
          message = data
        } else if (data?.detail) {
          message = data.detail
        }
      } else if (error.request) {
        // Network error
        message = 'Network error. Please check your connection and try again.'
      } else {
        message = error.message || message
      }

      // Transform error to our ApiError format
      const apiError: ApiError = {
        message,
        status: error.response?.status,
        errors: error.response?.data as Record<string, string[]>,
      }
      return Promise.reject(apiError)
    }
  )

  return client
}

const apiClient = createApiClient()

/**
 * Fetches merchant details by public ID
 * @param publicId - The unique merchant public ID
 * @returns Promise resolving to merchant data
 */
export async function fetchMerchant(publicId: string): Promise<MerchantData> {
  try {
    const endpoint = API_ENDPOINTS.MERCHANT(publicId)
    console.log('[API] fetchMerchant - Fetching merchant')
    console.log('[API] Endpoint:', endpoint)
    console.log('[API] Full URL:', `${API_BASE_URL}${endpoint}`)

    const response = await apiClient.get<MerchantApiResponse>(endpoint)

    console.log('[API] Merchant response:', response)
    console.log('[API] Response data:', response.data)
    console.log('[API] Response status:', response.status)

    // Extract the first merchant from the data array
    if (!response.data.data || response.data.data.length === 0) {
      console.error('[API] No merchant data found in response')
      throw new Error('Merchant not found')
    }

    console.log('[API] Merchant found:', response.data.data[0])
    return response.data.data[0]
  } catch (error) {
    console.error('[API] fetchMerchant error:', error)
    const apiError = error as ApiError

    // Provide more specific error messages
    if (apiError.status === 500) {
      throw new Error('Server error while fetching merchant. The backend may be restarting. Please refresh and try again.')
    } else if (apiError.status === 404) {
      throw new Error('Merchant not found. Please check the QR code.')
    }

    throw new Error(apiError.message || 'Failed to fetch merchant details')
  }
}

/**
 * Fetches user's saved payment details by device tag
 * @param deviceTag - The unique device identifier
 * @returns Promise resolving to user payment data or null if not found
 */
export async function fetchUserPaymentData(deviceTag: string): Promise<UserPaymentData | null> {
  try {
    const response = await apiClient.get<UserPaymentApiResponse>(API_ENDPOINTS.USER_DEVICE(deviceTag))
    
    // Extract the first user from the data array
    if (!response.data.data || response.data.data.length === 0) {
      return null
    }
    
    return response.data.data[0]
  } catch (error) {
    const apiError = error as ApiError
    // 404 means no saved data exists, which is fine
    if (apiError.status === 404) {
      return null
    }
    throw new Error(apiError.message || 'Failed to fetch user payment data')
  }
}

/**
 * Saves user's payment details with device tag (creates new user)
 * @param data - User payment data to save (tag, mobile_money object)
 * @returns Promise resolving to saved user payment data
 */
export async function saveUserPaymentData(data: {
  tag: string
  mobile_money: {
    operator: string
    mobile_number: string
  }
}): Promise<UserPaymentData> {
  try {
    const response = await apiClient.post<UserPaymentData>(API_ENDPOINTS.USER_DEVICE_CREATE, {
      tag: data.tag,
      mobile_money: data.mobile_money,
    })
    return response.data
  } catch (error) {
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to save user payment data')
  }
}

/**
 * Updates user's payment details by adding a new mobile money account (PATCH)
 * @param userId - The user ID
 * @param data - User payment data to update (tag, mobile_money object)
 * @returns Promise resolving to updated user payment data
 */
export async function updateUserPaymentData(
  userId: number,
  data: {
    tag: string
    mobile_money: {
      operator: string
      mobile_number: string
    }
  }
): Promise<UserPaymentData> {
  try {
    const endpoint = API_ENDPOINTS.USER_DEVICE_UPDATE(userId)
    const payload = {
      tag: data.tag,
      mobile_money: data.mobile_money,
    }

    console.log('[API] updateUserPaymentData - Making PATCH request')
    console.log('[API] Endpoint:', endpoint)
    console.log('[API] Payload:', payload)

    const response = await apiClient.patch<UserPaymentData>(endpoint, payload)

    console.log('[API] PATCH response:', response.data)
    return response.data
  } catch (error) {
    console.error('[API] PATCH error:', error)
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to update user payment data')
  }
}

/**
 * Submits a payment transaction (Legacy)
 * @param paymentData - Payment request data
 * @returns Promise resolving to payment response
 */
export async function submitPayment(paymentData: PaymentRequest): Promise<PaymentResponse> {
  try {
    const response = await apiClient.post<PaymentResponse>(API_ENDPOINTS.PAYMENT, paymentData)
    return response.data
  } catch (error) {
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to process payment')
  }
}

/**
 * Validates a QR payment payload
 * @param payload - QR payment payload
 * @returns Promise resolving to void (200 status means validation passed)
 */
export async function validateQRPayment(payload: QRPaymentPayload): Promise<void> {
  try {
    console.log('[API] validateQRPayment - Making POST request to validate')
    console.log('[API] Endpoint:', API_ENDPOINTS.PAYMENT_VALIDATE)
    console.log('[API] Payload:', payload)

    await apiClient.post(API_ENDPOINTS.PAYMENT_VALIDATE, payload)

    console.log('[API] Validation successful (200 response)')
  } catch (error) {
    console.error('[API] Validation error:', error)
    const apiError = error as ApiError

    // Extract error messages from validation response
    if (apiError.errors) {
      const errors = apiError.errors as Record<string, unknown>

      // Handle non_field_errors
      if (errors.non_field_errors && Array.isArray(errors.non_field_errors)) {
        throw new Error(errors.non_field_errors[0])
      }

      // Handle nested field errors
      const errorMessages: string[] = []
      Object.entries(errors).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errorMessages.push(`${key}: ${value[0]}`)
        } else if (typeof value === 'object' && value !== null) {
          // Handle nested errors like payer_account_details.currency
          Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
            if (Array.isArray(nestedValue)) {
              errorMessages.push(`${key}.${nestedKey}: ${nestedValue[0]}`)
            }
          })
        }
      })

      if (errorMessages.length > 0) {
        throw new Error(errorMessages.join(', '))
      }
    }

    throw new Error(apiError.message || 'Payment validation failed')
  }
}

/**
 * Processes a QR payment (after validation)
 * @param payload - QR payment payload
 * @returns Promise resolving to payment response
 */
export async function processQRPayment(payload: QRPaymentPayload): Promise<QRPaymentResponse> {
  try {
    console.log('[API] processQRPayment - Making POST request to pay')
    console.log('[API] Endpoint:', API_ENDPOINTS.PAYMENT_PAY)
    console.log('[API] Payload:', payload)

    const response = await apiClient.post<QRPaymentResponse>(API_ENDPOINTS.PAYMENT_PAY, payload)

    console.log('[API] Payment successful, response:', response.data)
    return response.data
  } catch (error) {
    console.error('[API] Payment error:', error)
    const apiError = error as ApiError

    // Extract error messages similar to validation
    if (apiError.errors) {
      const errors = apiError.errors as Record<string, unknown>

      if (errors.non_field_errors && Array.isArray(errors.non_field_errors)) {
        throw new Error(errors.non_field_errors[0])
      }

      const errorMessages: string[] = []
      Object.entries(errors).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errorMessages.push(`${key}: ${value[0]}`)
        } else if (typeof value === 'object' && value !== null) {
          Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
            if (Array.isArray(nestedValue)) {
              errorMessages.push(`${key}.${nestedKey}: ${nestedValue[0]}`)
            }
          })
        }
      })

      if (errorMessages.length > 0) {
        throw new Error(errorMessages.join(', '))
      }
    }

    throw new Error(apiError.message || 'Payment processing failed')
  }
}

/**
 * Get WebAuthn registration options from backend
 * @returns Promise resolving to registration options
 */
export async function getWebAuthnRegisterOptions(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  try {
    console.log('[API] Getting WebAuthn registration options')
    const response = await apiClient.get<PublicKeyCredentialCreationOptionsJSON>(
      API_ENDPOINTS.WEBAUTHN_REGISTER_OPTIONS
    )
    console.log('[API] Registration options received:', response.data)

    // Validate the response structure
    if (!response.data.challenge || !response.data.rp || !response.data.user) {
      console.error('[API] Invalid registration options structure:', response.data)
      throw new Error('Server returned invalid WebAuthn registration options')
    }

    return response.data
  } catch (error) {
    console.error('[API] Failed to get registration options:', error)
    const apiError = error as ApiError

    // Provide more specific error messages
    if (apiError.status === 500) {
      throw new Error('Server error while generating WebAuthn options. Please try again.')
    }

    throw new Error(apiError.message || 'Failed to get registration options')
  }
}

/**
 * Complete WebAuthn registration and get device tag
 * @param credential - Registration credential from WebAuthn
 * @param challenge - Original challenge from options
 * @param deviceInfo - Device information
 * @returns Promise resolving to device tag
 */
export async function completeWebAuthnRegistration(
  credential: RegistrationResponseJSON,
  challenge: string,
  deviceInfo: { platform: string; userAgent: string }
): Promise<{ deviceTag: string; credentialId: string }> {
  try {
    console.log('[API] Completing WebAuthn registration')
    const response = await apiClient.post<{ deviceTag: string; credentialId: string; success: boolean }>(
      API_ENDPOINTS.WEBAUTHN_REGISTER_COMPLETE,
      {
        credential,
        challenge,
        deviceInfo,
      }
    )
    console.log('[API] Registration completed, device tag:', response.data.deviceTag)
    return response.data
  } catch (error) {
    console.error('[API] Registration completion failed:', error)
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to complete registration')
  }
}

/**
 * Get WebAuthn authentication options from backend
 * @returns Promise resolving to authentication options
 */
export async function getWebAuthnAuthOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  try {
    console.log('[API] Getting WebAuthn authentication options')
    const response = await apiClient.get<PublicKeyCredentialRequestOptionsJSON>(
      API_ENDPOINTS.WEBAUTHN_AUTH_OPTIONS
    )

    // Handle case where backend returns a JSON string instead of object
    let options = response.data
    if (typeof options === 'string') {
      console.log('[API] Authentication options received as string, parsing...')
      options = JSON.parse(options)
    }

    console.log('[API] Authentication options received:', options)

    // Validate the response structure
    if (!options.challenge) {
      console.error('[API] Invalid authentication options structure:', options)
      throw new Error('Server returned invalid WebAuthn authentication options')
    }

    return options
  } catch (error) {
    console.error('[API] Failed to get authentication options:', error)
    const apiError = error as ApiError

    // Provide more specific error messages
    if (apiError.status === 500) {
      throw new Error('Server error while generating WebAuthn options. Please try again.')
    } else if (apiError.status === 404) {
      throw new Error('No registered devices found.')
    }

    throw new Error(apiError.message || 'Failed to get authentication options')
  }
}

/**
 * Complete WebAuthn authentication and get device tag + user data
 * @param credential - Authentication credential from WebAuthn
 * @param challenge - Original challenge from options
 * @returns Promise resolving to device tag and user payment data
 */
export async function completeWebAuthnAuthentication(
  credential: AuthenticationResponseJSON,
  challenge: string
): Promise<{ deviceTag: string; credentialId: string; userPaymentData: UserPaymentData | null }> {
  try {
    console.log('[API] Completing WebAuthn authentication')
    const response = await apiClient.post<{
      deviceTag: string
      credentialId: string
      userPaymentData: UserPaymentData | null
      success: boolean
    }>(API_ENDPOINTS.WEBAUTHN_AUTH_COMPLETE, {
      credential,
      challenge,
    })
    console.log('[API] Authentication completed, device tag:', response.data.deviceTag)
    return response.data
  } catch (error) {
    console.error('[API] Authentication completion failed:', error)
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to complete authentication')
  }
}

